import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getService } from '@/modules/Service/services'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Collapse, ErrorBoundary, Typogaphy } from 'vnetwork-ui'
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import PreviewTemplates from './Templates'

interface Props {
    serviceId?: number
    ticketId?: number
    ticketTypeKey?: string
    defaultExpanded?: boolean
    /** When true, renders as section within parent card (no outer border) */
    asSection?: boolean
}

// Template display names
const TEMPLATE_LABELS: Record<string, string> = {
    'SwiftFederation.CDN': 'CDN',
    'SwiftFederation.Transcoding': 'Transcoding',
    'SwiftFederation.Transmuxing': 'Transmuxing',
    'SwiftFederation.Recording': 'Recording',
    'SwiftFederation.StorageLFD': 'Storage LFD',
    'SwiftFederation.StorageOSS': 'Storage OSS',
    'Mlytics.Vnis': 'VNIS',
    'Email.Server': 'Email Server',
    'Email.EGPlatform': 'EG Platform',
    'Cloud.VPS': 'VPS',
    'Cloud.Hosting': 'Hosting',
    'Cloud.VCloud': 'VCloud',
    'Server.VBM': 'Bare Metal Server',
    'Server.Dedicated': 'Dedicated Server',
    'Server.Colocation': 'Colocation',
    'Domain.VNNIC': 'Domain VNNIC',
    'Domain.TLDs': 'Domain TLDs',
    'SSL.SSL': 'SSL',
    'Device.Liquidation': 'Device Liquidation',
}

// Unified template color - consistent slate gray
const DEFAULT_TEMPLATE_COLOR = 'bg-slate-100 text-slate-700'

const PreviewServiceInformation: React.FC<Props> = ({
    serviceId,
    ticketTypeKey,
    defaultExpanded = false,
    asSection = false,
}) => {
    const { t } = useTranslation()
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)

    const {
        data: service,
        isLoading,
        isError,
    } = useQueryWithHandler({
        queryFn: () => getService(serviceId as number),
        queryKey: [QUERY_KEYS.SERVICE_DETAIL, serviceId, 'preview'],
        enabled: !!serviceId,
    })

    if (!serviceId) return null

    const wrapperClass = asSection
        ? 'border-t border-b border-gray-200'
        : 'bg-slate-50 rounded-md border border-slate-200 overflow-hidden'

    if (isLoading) {
        return (
            <div
                className={`p-3 ${asSection ? 'border-t border-gray-200' : 'bg-slate-50 rounded-md border border-slate-200'} space-y-3`}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-5 w-16" />
                </div>
            </div>
        )
    }

    if (isError || !service) {
        return (
            <div
                className={`p-3 ${asSection ? 'border-t border-gray-200 bg-red-50' : 'bg-red-50 rounded-md border border-red-200'}`}
            >
                <Typogaphy size="small" className="text-red-600">
                    {t('service-config-load-error')}
                </Typogaphy>
            </div>
        )
    }

    // Get template label
    const templateLabel = service.service_template
        ? TEMPLATE_LABELS[service.service_template] || service.service_template
        : null

    // Get the appropriate template Information component
    const Template =
        PreviewTemplates[
            service.service_template as keyof typeof PreviewTemplates
        ] || PreviewTemplates['Blank']
    const Information = Template?.Information

    const serviceDetailUrl = generatePathDatacentral(paths.serviceDetail, {
        id: String(service.service_id),
    })

    return (
        <div className={wrapperClass}>
            {/* Collapsible Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`w-full p-3 flex items-center justify-between transition-colors ${asSection ? 'hover:bg-gray-50' : 'hover:bg-slate-100'}`}
            >
                <div className="flex items-center gap-2">
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                    )}
                    <div className="flex items-center gap-2 text-left">
                        <Typogaphy
                            size="small"
                            className="text-slate-700 font-medium"
                        >
                            {t('service-configuration-preview')}
                        </Typogaphy>
                        <span className="text-sm font-medium text-primary">
                            #{service.service_id} - {service.service_name}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {templateLabel && (
                        <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${DEFAULT_TEMPLATE_COLOR}`}
                        >
                            {templateLabel}
                        </span>
                    )}
                    <Link
                        to={serviceDetailUrl}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-primary"
                        title={t('view-service-detail')}
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </button>

            {/* Collapsible Content */}
            <Collapse open={isExpanded}>
                <div
                    className={`p-2 ${asSection ? 'border-t border-gray-100' : 'border-t border-slate-200'}`}
                >
                    <ErrorBoundary>
                        <Information
                            service={service}
                            ticketTypeKey={ticketTypeKey}
                        />
                    </ErrorBoundary>
                </div>
            </Collapse>
        </div>
    )
}

export default PreviewServiceInformation
